import { Loader2, Search } from "lucide-react";
import { DealContent, LeadContent, PageContent, RecordingContent } from "./item-contents";
import ResultRow, { SectionLabel } from "./result-row";
import { hrefFor, SearchItem } from "./types";
import { useSearchResults } from "./use-search-results";

type Props = {
  results: ReturnType<typeof useSearchResults>;
  activeIdx: number;
  setActiveIdx: (idx: number) => void;
  onSelect: (item: SearchItem) => void;
};

const keyOf = (item: SearchItem) => {
  if (item.kind === "page") return `page-${item.page.path}`;
  if (item.kind === "lead") return `lead-${item.lead.id}`;
  if (item.kind === "deal") return `deal-${item.deal.id}`;
  return `recording-${item.recording.ref}`;
};

const countLabel = (label: string, shown: number, total: number) =>
  `${label} (${shown}${total > shown ? ` of ${total}` : ""})`;

/** Result list: pages, then leads, deals and recordings, each group labelled. */
const SearchResults = ({ results, activeIdx, setActiveIdx, onSelect }: Props) => {
  const { items, hasQuery, loading, trimmed, pageMatches, leads, deals, recordings } = results;

  if (hasQuery && !loading && items.length === 0) {
    return (
      <div className="grid place-items-center px-6 py-12 text-center">
        <div className="grid h-12 w-12 place-items-center rounded-xl border border-white/[0.06] bg-white/[0.02] text-slate-400">
          <Search className="h-5 w-5" />
        </div>
        <div className="mt-3 text-[14px] font-semibold text-white">No results for “{trimmed}”</div>
        <p className="mt-1 max-w-xs text-[11.5px] text-slate-500">Try a lead name, deal name, email or phone number.</p>
      </div>
    );
  }

  const firstIdx = (kind: SearchItem["kind"]) => items.findIndex((i) => i.kind === kind);
  const sectionFor = (item: SearchItem, idx: number) => {
    if (idx !== firstIdx(item.kind) || item.kind === "page") return null;
    if (item.kind === "lead") return countLabel("Leads", leads.items.length, leads.total);
    if (item.kind === "deal") return countLabel("Deals", deals.items.length, deals.total);
    return countLabel("Recordings", recordings.items.length, recordings.total);
  };

  return (
    <>
      {pageMatches.length > 0 && <SectionLabel>{hasQuery ? "Pages" : "Jump to"}</SectionLabel>}
      {items.map((item, idx) => {
        const section = sectionFor(item, idx);
        return (
          <div key={keyOf(item)}>
            {section && <SectionLabel>{section}</SectionLabel>}
            <ResultRow
              href={hrefFor(item)}
              idx={idx}
              active={idx === activeIdx}
              onNavigate={() => onSelect(item)}
              onHover={() => setActiveIdx(idx)}
            >
              {item.kind === "page" && <PageContent page={item.page} />}
              {item.kind === "lead" && <LeadContent lead={item.lead} />}
              {item.kind === "deal" && <DealContent deal={item.deal} />}
              {item.kind === "recording" && <RecordingContent recording={item.recording} />}
            </ResultRow>
          </div>
        );
      })}
      {hasQuery && loading && leads.items.length + deals.items.length + recordings.items.length === 0 && (
        <div className="flex items-center gap-2 px-4 py-3 text-[11.5px] text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Searching...
        </div>
      )}
    </>
  );
};

export default SearchResults;
